export interface FormatOptions {
  locale?: string;
  timeZone?: string;
}

/**
 * Formats an instant in the visitor's own locale and time zone: time only when
 * it falls on today's date, otherwise weekday, date and time (with the year
 * when it is not the current one). Returns an empty string for an unparseable
 * value. `locale` and `timeZone` default to the browser's own.
 */
export function formatLocalTime(
  iso: string,
  now: Date = new Date(),
  options: FormatOptions = {},
): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const { locale, timeZone } = options;
  const zone: Intl.DateTimeFormatOptions = timeZone ? { timeZone } : {};
  const day = (d: Date, extra: Intl.DateTimeFormatOptions) =>
    d.toLocaleDateString(locale, { ...zone, ...extra });

  const time: Intl.DateTimeFormatOptions = {
    ...zone,
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  };
  const full: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'numeric', day: 'numeric' };

  if (day(date, full) === day(now, full)) return date.toLocaleTimeString(locale, time);

  const sameYear = day(date, { year: 'numeric' }) === day(now, { year: 'numeric' });
  return date.toLocaleString(locale, {
    ...time,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}
